"use strict";
/**
 * Pad 2, layout E+M (ai/nba/N8-taps/PLAN.md §19.5; draft ai/nba/N8-taps/drafts/tap-pad-drafts.html,
 * chip "E+M"). Markup once, then `paintNbaPadV2` changes text, classes and attributes only: a
 * button is never replaced, so nothing moves under a finger that fires on pointerdown.
 *
 * Buttons keep their base kind in `data-tap-kind` (2PT is always `shot_2`); the cue's kind is
 * `data-tap-now`, for display only: the kind recorded is decided at the tap (`nbaV2Shot`), never read
 * back from a paint that may lag. The home half mirrors by `data-slot` in CSS; its DOM order is
 * the away half's. Hold targets carry `.nba-em-hold` and `data-hold` (their role, read by the
 * handler); the hold helper sets `.hold-filling` and `--hold-ms` on them while held.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.renderNbaTapPadV2 = renderNbaTapPadV2;
exports.nbaPadV2PeriodLabel = nbaPadV2PeriodLabel;
exports.paintNbaPadV2 = paintNbaPadV2;
exports.paintNbaPadV2Track = paintNbaPadV2Track;
const nba_tap_pad_1 = require("./nba-tap-pad");
const tap_parked_1 = require("./tap-parked");
function escapeHtml(value) {
    return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}
/** The banner's ball and the Ball button's icon (the draft's). */
const BALL_SVG = `<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6.5"/><path d="M1.5 8h13M8 1.5v13M3.4 3.4c2.6 2 2.6 7.2 0 9.2M12.6 3.4c-2.6 2-2.6 7.2 0 9.2"/></svg>`;
function play(kind, slot, side, cls, inner) {
    return `<button type="button" class="nba-tap-btn ${cls}" data-tap-kind="${kind}" data-tap-now="${kind}" data-side="${side}" data-slot="${slot}">${inner}</button>`;
}
/** A swapping FT button in Pad 1's hook classes, so the handler's mode, gate and disabling apply as on Pad 1. */
function ftSwap(kind, side, cell) {
    const mode = (0, nba_tap_pad_1.nbaFtButtonMode)(kind);
    const group = mode === "trip" ? "nba-ft-trip-btn" : mode === "tech" ? "nba-ft-tech-attempt" : "nba-ft-attempt";
    const off = mode === "trip" ? "" : " disabled";
    return `<button type="button" class="nba-tap-btn nba-ft-btn nba-ft-swaps ${group}" data-tap-kind="${kind}" data-side="${side}" data-ft-cell="${cell}"${off}>${escapeHtml(nba_tap_pad_1.NBA_TAP_LABELS[kind])}</button>`;
}
function half(side, team) {
    const label = (text) => `<span class="nba-em-label">${text}</span>`;
    return `<section class="nba-em-half" data-side="${side}">
			<div class="nba-em-band"><span class="nba-em-team">${side === "home" ? "HOME" : "AWAY"} · ${escapeHtml(team)}</span><span class="nba-em-pill" aria-hidden="true">${BALL_SVG}BALL</span></div>
			<div class="nba-em-grid">
				${play("shot_2", "shot2", side, "nba-em-shot", label("2PT"))}${play("shot_3", "shot3", side, "nba-em-shot", label("3PT"))}
				${play("ball", "ball", side, "nba-em-ball", `${BALL_SVG}<span class="nba-em-ball-label">Ball</span>`)}
				${play("missed", "missed", side, "nba-em-missed", label("Missed"))}${play("off_rebound", "offreb", side, "nba-em-offreb", label("Off Reb"))}
				<div class="nba-ft-area nba-em-ft" data-side="${side}" data-mode="trip" data-slot="ft">
					${ftSwap("ft_trip_1", side, "a")}${ftSwap("ft_trip_2", side, "b")}${ftSwap("ft_trip_3", side, "c")}
					${ftSwap("ft_made", side, "a")}${ftSwap("ft_missed", side, "b")}
					${ftSwap("ft_tech_made", side, "a")}${ftSwap("ft_tech_missed", side, "b")}
					<span class="nba-ft-progress" data-ft-cell="c" aria-live="polite"></span>
					<button type="button" class="nba-tap-btn nba-ft-btn nba-em-tech" data-tap-kind="ft_tech" data-side="${side}" data-ft-cell="q">${escapeHtml(nba_tap_pad_1.NBA_TAP_LABELS.ft_tech)}</button>
				</div>
			</div>
		</section>`;
}
function renderNbaTapPadV2(teams, title) {
    return `<div class="nba-pad-em" data-pad-version="2">
		<div class="nba-em-portrait-hint">Turn the phone to landscape</div>
		<div class="nba-em-halves">
			${half("away", teams.away)}
			<div class="nba-em-centre">
				<button type="button" class="nba-tap-btn nba-em-pull nba-em-hold" data-tap-kind="pull" data-slot="pull" data-hold="resume" aria-label="Pull quotes"><span class="nba-em-pull-label">PULL</span><small class="nba-em-pull-sub"></small></button>
				<div class="nba-em-board" data-slot="board" data-score="loading">
					<span class="nba-em-score-main">loading</span>
					<span class="nba-em-score-sub"></span>
					<button type="button" class="nba-em-period nba-em-hold" data-hold="period" aria-label="Next period (press and hold)">Q?</button>
				</div>
				<button type="button" class="nba-tap-btn nba-em-undo" data-tap-kind="undo" data-slot="undo">UNDO</button>
				<div class="nba-em-tip" data-slot="tip" role="group" aria-label="Opening tip" hidden>
					<span class="nba-em-tip-q">Recording from the opening tip?</span>
					<button type="button" class="nba-em-tip-btn" data-tip="yes" aria-pressed="false">Yes</button>
					<button type="button" class="nba-em-tip-btn" data-tip="no" aria-pressed="false">No</button>
				</div>
			</div>
			${half("home", teams.home)}
		</div>
		<div class="nba-em-foot">
			<button type="button" class="nba-em-exit nba-em-hold" data-hold="exit" aria-label="Exit (press and hold)">Exit</button>
			<span class="nba-em-title">${escapeHtml(title)}</span>
			<button type="button" class="nba-em-track nba-em-hold" data-hold="track-stop" disabled>Start tracking (waiting for clock)</button>
			${tap_parked_1.PARKED_HTML}
			<span class="nba-track-note nba-em-track-note" role="status"></span>
			<span class="nba-tap-status nba-em-status" role="status"></span>
			<span class="nba-last nba-em-last"></span>
			<div class="nba-ft-state nba-em-ft-state" role="status" aria-live="polite">${(0, nba_tap_pad_1.renderNbaFtState)({ trip: { kind: "unknown" }, tech: null }, teams)}</div>
		</div>
	</div>`;
}
/** Writes text only when it changed, so an unchanged paint touches no node. */
function setText(el, text) {
    if (el && el.textContent !== text)
        el.textContent = text;
}
function nbaPadV2PeriodLabel(period) {
    if (period === null)
        return "Q?";
    if (period <= 4)
        return `Q${period}`;
    return period === 5 ? "OT" : `OT${period - 4}`;
}
function paintSide(root, side, v, other, team) {
    const half = root.querySelector(`.nba-em-half[data-side="${side}"]`);
    if (!half)
        return;
    const slot = (s) => half.querySelector(`[data-slot="${s}"]`);
    for (const [s, cue] of [["shot2", v.shot2], ["shot3", v.shot3]]) {
        const b = slot(s);
        if (!b)
            continue;
        setText(b.querySelector(".nba-em-label"), cue.label);
        b.dataset.tapNow = cue.kind;
        b.classList.toggle("is-lit", cue.lit);
        b.classList.toggle("is-faint", cue.faint);
    }
    const ball = slot("ball");
    setText(ball?.querySelector(".nba-em-ball-label") ?? null, v.ballLabel);
    ball?.classList.toggle("is-lit", v.ballLabel === "D-REB");
    slot("missed")?.classList.toggle("is-lit", v.missedLit);
    slot("offreb")?.classList.toggle("is-lit", v.offRebLit);
    const pill = half.querySelector(".nba-em-pill");
    if (pill) {
        pill.classList.toggle("is-on", v.hasBall);
        pill.classList.toggle("is-uncertain", v.hasBall && v.possUncertain);
        if (v.hasBall) {
            pill.removeAttribute("aria-hidden");
            pill.setAttribute("aria-label", `${team} has the ball${v.possUncertain ? " (uncertain)" : ""}`);
        }
        else {
            pill.setAttribute("aria-hidden", "true");
            pill.removeAttribute("aria-label");
        }
    }
    half.querySelector(".nba-em-band")?.classList.toggle("is-off", other.hasBall && !v.hasBall);
}
/** Paints a view (nba-pad-v2-view.ts) onto rendered Pad 2 markup under `root`. */
function paintNbaPadV2(root, view) {
    const pad = root.querySelector(".nba-pad-em");
    if (!pad)
        return;
    const team = (s) => pad.querySelector(`.nba-em-half[data-side="${s}"] .nba-em-team`)?.textContent?.replace(/^(HOME|AWAY) · /, "") ?? s;
    paintSide(pad, "away", view.away, view.home, team("away"));
    paintSide(pad, "home", view.home, view.away, team("home"));
    const board = pad.querySelector(".nba-em-board");
    if (board) {
        const s = view.score;
        board.dataset.score = s.kind;
        const main = s.kind === "loading" ? "loading" : s.kind === "actual" ? `${s.away}–${s.home}` : `+${s.away} / +${s.home}`;
        const sub = s.kind === "loading" ? "" : s.kind === "actual" ? `${team("away")} · ${team("home")}` : `since ${s.since}`;
        setText(board.querySelector(".nba-em-score-main"), main);
        setText(board.querySelector(".nba-em-score-sub"), sub);
        setText(board.querySelector(".nba-em-period"), nbaPadV2PeriodLabel(view.period));
    }
    const pull = pad.querySelector(".nba-em-pull");
    if (pull) {
        const pulled = view.pulled === true;
        pull.classList.toggle("is-pulled", pulled);
        setText(pull.querySelector(".nba-em-pull-label"), pulled ? "PULLED" : "PULL");
        setText(pull.querySelector(".nba-em-pull-sub"), pulled ? "hold to resume" : view.pulled === null ? "record loading" : "");
        pull.setAttribute("aria-label", pulled ? "Quotes pulled; press and hold to resume" : view.pulled === null ? "Pull quotes (record loading)" : "Pull quotes");
    }
    const tip = pad.querySelector(".nba-em-tip");
    if (tip) {
        tip.hidden = view.tipPrompt === null;
        const pre = view.tipPrompt?.preselect ?? null;
        tip.dataset.answered = String(view.tipPrompt?.answered === true);
        tip.querySelector('[data-tip="yes"]')?.setAttribute("aria-pressed", String(pre === true));
        tip.querySelector('[data-tip="no"]')?.setAttribute("aria-pressed", String(pre === false));
    }
}
/** The footer's tracking control, from `renderNbaTrack` (Start is a tap, Stop a hold: the handler's). */
function paintNbaPadV2Track(root, t) {
    const btn = root.querySelector(".nba-pad-em .nba-em-track");
    if (btn) {
        setText(btn, t.label);
        btn.disabled = t.disabled;
        btn.classList.toggle("is-on", t.on);
    }
    setText(root.querySelector(".nba-pad-em .nba-em-track-note"), t.note);
}
